import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, ArrowRight, Sparkles, BookOpen, CheckCircle } from 'lucide-react';
import { ScrollReveal } from './ScrollAnimations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResourcesPromoSection = ({ variant = 'full' }) => {
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState({ total: 0, downloads: 0 });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/resources`);
      if (res.ok) {
        const data = await res.json();
        setResources(data.slice(0, 3));
        const totalDownloads = data.reduce((acc, r) => acc + (r.downloads || 0), 0);
        setStats({ total: data.length, downloads: totalDownloads });
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  // Compact variant for blog sidebar or smaller spaces
  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-br from-accent/10 via-purple-500/5 to-transparent border border-accent/20 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
            <Download className="text-accent" size={20} />
          </div>
          <div>
            <h3 className="text-white font-semibold">Ressources Gratuites</h3>
            <p className="text-white/50 text-xs">{stats.total} guides disponibles</p>
          </div>
        </div>
        
        <p className="text-white/60 text-sm mb-4">
          Téléchargez nos guides SEO, checklists et templates pour booster votre référencement.
        </p>
        
        <Link 
          to="/ressources"
          className="inline-flex items-center gap-2 w-full justify-center px-4 py-3 bg-accent text-black font-medium hover:bg-accent/90 transition-colors text-sm"
        >
          <Download size={16} />
          Accéder aux ressources
        </Link>
      </div>
    );
  }

  // Banner variant for inline promotion
  if (variant === 'banner') {
    return (
      <div className="bg-gradient-to-r from-accent/10 via-purple-500/10 to-accent/10 border-y border-accent/20 py-6">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="text-accent" size={24} />
              </div>
              <div>
                <h3 className="text-white font-semibold">
                  Boostez votre SEO avec nos ressources gratuites
                </h3>
                <p className="text-white/50 text-sm">
                  {stats.total} guides, checklists et templates à télécharger
                </p>
              </div>
            </div>
            <Link 
              to="/ressources"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-black font-medium hover:bg-accent/90 transition-colors whitespace-nowrap"
            >
              <Download size={18} />
              Télécharger gratuitement
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Full variant for homepage
  return (
    <section className="relative bg-[#050505] py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-accent/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[200px]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        <ScrollReveal animation="fadeUp">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/20 text-accent text-sm font-medium mb-6">
              <Download size={16} />
              Ressources Gratuites
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-4">
              Téléchargez nos <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-purple-400">guides SEO</span>
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Des ressources exclusives pour améliorer votre référencement. 
              Guides, checklists et templates créés par nos experts.
            </p>
          </div>
        </ScrollReveal>

        {/* Stats */}
        <ScrollReveal animation="fadeUp" delay={100}>
          <div className="flex justify-center gap-8 mb-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-accent">{stats.total}</div>
              <div className="text-white/50 text-sm">Ressources</div>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <div className="text-3xl font-bold text-accent">{stats.downloads}+</div>
              <div className="text-white/50 text-sm">Téléchargements</div>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <div className="text-3xl font-bold text-accent">100%</div>
              <div className="text-white/50 text-sm">Gratuit</div>
            </div>
          </div>
        </ScrollReveal>

        {/* Resources Preview */}
        {resources.length > 0 && (
          <ScrollReveal animation="fadeUp" delay={200}>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {resources.map((resource, index) => (
                <div 
                  key={resource.id}
                  className="group bg-white/[0.02] border border-white/10 p-6 hover:border-accent/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                      {resource.file_type === 'PDF' ? (
                        <FileText className="text-accent" size={24} />
                      ) : (
                        <BookOpen className="text-accent" size={24} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-accent text-xs font-medium">{resource.category}</span>
                      <h3 className="text-white font-semibold mt-1 mb-2 line-clamp-2 group-hover:text-accent transition-colors">
                        {resource.title}
                      </h3>
                      <div className="flex items-center gap-2 text-white/40 text-xs">
                        <span className="px-2 py-0.5 bg-white/5 rounded">{resource.file_type}</span>
                        <span>{resource.downloads || 0} téléchargements</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        )}

        {/* CTA */}
        <ScrollReveal animation="fadeUp" delay={300}>
          <div className="text-center">
            <Link 
              to="/ressources"
              className="inline-flex items-center gap-3 px-8 py-4 bg-accent text-black font-semibold hover:bg-accent/90 transition-all duration-300 group"
            >
              <Download size={20} />
              Voir toutes les ressources
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="text-white/40 text-sm mt-4">
              <CheckCircle size={14} className="inline mr-1 text-green-400" />
              Accès immédiat après inscription
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ResourcesPromoSection;
